/**
 * @fileoverview Step 2: File Your Appeal Page
 * @module Step1FilePage
 * 
 * Business Context:
 * - Guides users through the appeal filing process
 * - Covers submission methods (Mail vs Electronic Portal)
 * - Explains how to fill out the protest form (50-132)
 * - This is Step 2 in the 4-step journey (Document → File → Flaws → Arguments)
 * 
 * Layout:
 * - LEFT: Course navigation (video list + activity)
 * - RIGHT: Video player OR inline activity
 * 
 * Content Structure:
 * 1. How to Fill Out Your Protest Form (video content) - collapsible
 * 2. Submit Your Appeal (activity) - collapsible
 * 
 * Progress Persistence:
 * - Progress is saved to backend via apiClient.courses.updateProgress
 * - Progress is loaded from backend on mount
 * - When all items are complete, module is marked complete
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import VideoPlayer from '../components/course/VideoPlayer';
import { FileAppealActivity } from '../components/appeal';
import {
  ArrowLeft,
  FileText,
  CheckCircle2,
  Play,
  Clock,
  ChevronRight,
  ChevronDown,
  Loader2,
  AlertCircle,
  BookOpen,
  Send
} from 'lucide-react';

// Module ID for Step 2 (File Appeal) - module-01 contains File Appeal content
const STEP1_MODULE_ID = 'module-01';

interface VideoSection {
  id: string;
  title: string;
  description: string;
  duration: string;
  videoUrl: string;
  completed: boolean;
}

const Step1FilePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [videoSections, setVideoSections] = useState<VideoSection[]>([]);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [completedSections, setCompletedSections] = useState<Set<string>>(new Set());
  
  // Collapsible state for sections
  const [isVideosExpanded, setIsVideosExpanded] = useState(true);
  const [isActivityExpanded, setIsActivityExpanded] = useState(false);
  
  // Activity state
  const [activeActivity, setActiveActivity] = useState<string | null>(null);
  const [completedActivities, setCompletedActivities] = useState<Set<string>>(new Set());

  // Track if progress has been initialized from backend
  const progressInitialized = useRef(false);
  // Track pending save operations
  const saveInProgress = useRef(false);
  // Save status for user feedback
  const [saveStatus, setSaveStatus] = useState<'saving' | 'saved' | 'error' | null>(null);

  // Load Montserrat font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  // Save progress to backend
  // BUSINESS LOGIC: Only the activity completion matters for module progress
  // - Activity complete = 100% module progress (module is done)
  // - Videos are supplementary and tracked separately in frontend state only
  const saveProgress = useCallback(async (
    completedVideoIds: Set<string>,
    completedActivityIds: Set<string>,
    totalVideos: number
  ) => {
    if (saveInProgress.current) return;
    saveInProgress.current = true;
    setSaveStatus('saving');
    
    try {
      // Module completion is based ONLY on the activity
      // Activity complete = 100%, Activity not complete = 0%
      const isActivityComplete = completedActivityIds.has('file-appeal');
      const progressPercentage = isActivityComplete ? 100 : 0;

      console.log(`📊 Saving Step 2 (File Appeal) progress: ${progressPercentage}% (Activity complete: ${isActivityComplete}, Videos watched: ${completedVideoIds.size}/${totalVideos})`);

      // Save progress to backend
      await apiClient.courses.updateProgress(STEP1_MODULE_ID, {
        progressPercentage,
        timeSpent: 0 // We're not tracking time for Step 2 currently
      });

      // If activity is complete, mark module as complete
      if (isActivityComplete) {
        console.log('✅ Step 2 (File Appeal) activity complete - marking module as complete');
        await apiClient.courses.completeModule(STEP1_MODULE_ID);
      }

      console.log(`✅ Step 2 (File Appeal) progress saved: ${progressPercentage}%`);
      setSaveStatus('saved');
      // Clear saved status after 2 seconds
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      console.error('Failed to save Step 2 (File Appeal) progress:', err);
      setSaveStatus('error');
      // Clear error status after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      saveInProgress.current = false;
    }
  }, []);

  // Restore progress state from a progress percentage
  // BUSINESS LOGIC: progress >= 100 means activity is complete
  // Videos are tracked separately in frontend state only (not persisted)
  const restoreProgressFromPercentage = useCallback((
    progressPercentage: number,
    sections: VideoSection[]
  ): { completedVideos: Set<string>; completedActivities: Set<string> } => {
    const completedVideos = new Set<string>();
    const completedActivities = new Set<string>();
    
    // Activity is complete if progress is 100%
    if (progressPercentage >= 100) {
      completedActivities.add('file-appeal');
    }
    
    // Note: Video completion is not persisted to backend
    // Videos will show as incomplete on page reload (by design - they're supplementary)
    
    return { completedVideos, completedActivities };
  }, []);

  // Fetch module content for Step 2 (File Your Appeal)
  // Also checks the notice gate - user must have received their Notice of Appraised Value
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        setError(null);

        // NOTICE GATE: Check if user has received their Notice of Appraised Value
        // Step 2 (File Your Appeal) should only be accessible after notice is received
        try {
          const profileRes = await apiClient.users.getProfile();
          const profile = profileRes.data.data.profile || profileRes.data.data;
          const userProperty = profile.properties?.[0];
          if (!userProperty?.lastAppraisalNoticeDate) {
            console.log('⚠️ Notice not received - redirecting to dashboard');
            navigate('/dashboard', { replace: true });
            return;
          }
        } catch (profileErr) {
          console.error('Failed to check notice status:', profileErr);
          // On error, allow access (don't block the user) - dashboard will gate if needed
        }

        // Fetch module 1 for Step 1 content
        const moduleRes = await apiClient.courses.getModule(STEP1_MODULE_ID, {});

        const sections: VideoSection[] = [];

        // Process module 1 content
        if (moduleRes.data.success && moduleRes.data.data.content) {
          moduleRes.data.data.content.forEach((content: any, idx: number) => {
            sections.push({
              id: content.id || `m1-${idx}`,
              title: content.metadata?.title || `Section ${idx + 1}`,
              description: content.metadata?.description || '',
              duration: formatDuration(content.metadata?.duration || 0),
              videoUrl: content.contentUrl || '',
              completed: false
            });
          });
        }

        setVideoSections(sections);
        
        // Restore progress from backend if available
        const savedProgress = moduleRes.data.data.progress || 0;
        if (savedProgress > 0 && !progressInitialized.current) {
          progressInitialized.current = true;
          console.log(`🔄 Restoring Step 2 (File Appeal) progress: ${savedProgress}%`);
          
          const { completedVideos, completedActivities: restoredActivities } = 
            restoreProgressFromPercentage(savedProgress, sections);
          
          setCompletedSections(completedVideos);
          setCompletedActivities(restoredActivities);
          
          console.log(`📋 Restored: ${completedVideos.size} videos, ${restoredActivities.size} activities`);
          
          // If activity is complete but there are incomplete videos, expand videos
          // If all videos complete but activity is not, expand activity
          if (completedVideos.size === sections.length && !restoredActivities.has('file-appeal')) {
            setIsActivityExpanded(true);
            setIsVideosExpanded(false);
          }
        }
        
        // Auto-select first incomplete section if available
        if (sections.length > 0) {
          const { completedVideos } = restoreProgressFromPercentage(savedProgress, sections);
          const firstIncomplete = sections.find(s => !completedVideos.has(s.id));
          setActiveSection(firstIncomplete?.id || sections[0].id);
        }
      } catch (err: any) {
        console.error('Error fetching Step 2 (File Appeal) content:', err);
        setError('Failed to load course content. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchContent();
    }
  }, [user, restoreProgressFromPercentage]);

  const formatDuration = (seconds: number): string => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVideoComplete = useCallback(() => {
    if (activeSection) {
      const newCompleted = new Set(completedSections);
      newCompleted.add(activeSection);
      setCompletedSections(newCompleted);

      // Save progress to backend
      saveProgress(newCompleted, completedActivities, videoSections.length);

      // Auto-advance to next section
      const currentIndex = videoSections.findIndex(s => s.id === activeSection);
      if (currentIndex < videoSections.length - 1) {
        setTimeout(() => {
          setActiveSection(videoSections[currentIndex + 1].id);
        }, 1500);
      } else if (newCompleted.size === videoSections.length && !completedActivities.has('file-appeal')) {
        // All videos complete, expand activity section
        setTimeout(() => {
          setIsActivityExpanded(true);
          setIsVideosExpanded(false);
        }, 1500);
      }
    }
  }, [activeSection, completedSections, completedActivities, videoSections, saveProgress]);

  const getActiveVideo = (): VideoSection | undefined => {
    return videoSections.find(s => s.id === activeSection);
  };

  const calculateProgress = (): number => {
    // Module progress is based ONLY on activity completion
    // Activity complete = 100%, Activity not complete = 0%
    return completedActivities.has('file-appeal') ? 100 : 0;
  };

  // Handle activity selection
  const handleActivitySelect = (activityId: string) => {
    setActiveActivity(activityId);
    setActiveSection(null); // Clear video when showing activity
  };

  // Handle completing the activity
  const handleActivityComplete = useCallback(() => {
    const newCompleted = new Set(completedActivities);
    newCompleted.add('file-appeal');
    setCompletedActivities(newCompleted);

    // Save progress to backend (this will also mark module complete if all items are done)
    saveProgress(completedSections, newCompleted, videoSections.length);
  }, [completedActivities, completedSections, videoSections.length, saveProgress]);

  // Go back to video from activity
  const handleBackFromActivity = () => {
    setActiveActivity(null);
    if (videoSections.length > 0) {
      setActiveSection(videoSections[0].id);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'system-ui, sans-serif' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-teal-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading Step 2 content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'system-ui, sans-serif' }}>
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Error Loading Content</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-slate-900 text-white px-6 py-2 rounded-lg font-medium hover:bg-slate-800 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Dashboard</span>
            </button>

            {/* Progress */}
            <div className="flex items-center gap-4">
              <div className="text-sm text-slate-600">
                <span className="font-bold text-teal-600">{calculateProgress()}%</span> complete
              </div>
              {/* Save Status Indicator */}
              {saveStatus && (
                <div className={`text-xs font-medium px-2 py-1 rounded transition-all ${
                  saveStatus === 'saving' ? 'text-slate-500 bg-slate-100' :
                  saveStatus === 'saved' ? 'text-emerald-600 bg-emerald-50' :
                  'text-red-600 bg-red-50'
                }`}>
                  {saveStatus === 'saving' ? 'Saving...' :
                   saveStatus === 'saved' ? '✓ Saved' :
                   '✗ Save failed'}
                </div>
              )}
              <div className="w-32 h-2 bg-slate-200 rounded-full">
                <div 
                  className="h-full bg-teal-500 rounded-full transition-all duration-500"
                  style={{ width: `${calculateProgress()}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Main Content - Left Navigation, Right Video/Activity */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* LEFT SIDE - Navigation & Activities */}
          <div className="lg:col-span-4 order-2 lg:order-1">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden sticky top-24">
              {/* Step Header - Primary Identity */}
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="pl-6">
                  <p className="text-[10px] font-bold text-teal-600 uppercase tracking-widest mb-0.5">Step 2</p>
                  <h2 className="text-base font-bold text-slate-800 leading-tight">File Your Appeal</h2>
                </div>
              </div>

              {/* Video Navigation Section - Collapsible */}
              <div className="border-b border-slate-100">
                {/* Collapsible Header */}
                <button
                  onClick={() => setIsVideosExpanded(!isVideosExpanded)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-teal-600" />
                    <h3 className="text-sm font-semibold text-slate-700">How to Fill Out Your Protest Form</h3>
                    <span className="text-xs text-slate-400 ml-1">
                      ({completedSections.size}/{videoSections.length})
                    </span>
                  </div>
                  <ChevronDown 
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                      isVideosExpanded ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                
                {/* Collapsible Content */}
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isVideosExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="px-4 pb-4 space-y-2">
                    {videoSections.map((section, index) => {
                      const isActive = section.id === activeSection && !activeActivity;
                      const isCompleted = completedSections.has(section.id);

                      return (
                        <button
                          key={section.id}
                          onClick={() => {
                            setActiveSection(section.id);
                            setActiveActivity(null); // Clear activity when selecting video
                          }}
                          className={`w-full text-left p-3 rounded-xl transition-all ${
                            isActive
                              ? 'bg-teal-50 border-2 border-teal-300'
                              : isCompleted
                              ? 'bg-emerald-50 border-2 border-emerald-200'
                              : 'bg-slate-50 border-2 border-slate-100 hover:border-slate-200'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              isCompleted ? 'bg-emerald-200' : isActive ? 'bg-teal-200' : 'bg-slate-200'
                            }`}>
                              {isCompleted ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                              ) : isActive ? (
                                <Play className="w-4 h-4 text-teal-700" />
                              ) : (
                                <span className="text-slate-600 font-bold text-sm">{index + 1}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className={`font-medium text-sm leading-tight ${
                                isActive ? 'text-teal-900' : isCompleted ? 'text-emerald-800' : 'text-slate-900'
                              }`}>
                                {section.title}
                              </h4>
                              <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                                <Clock className="w-3 h-3" />
                                <span>{section.duration}</span>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Submit Your Appeal Activity Section - Collapsible */}
              <div className="bg-gradient-to-b from-teal-50/50 to-white">
                {/* Collapsible Header */}
                <button
                  onClick={() => setIsActivityExpanded(!isActivityExpanded)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-teal-100/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Send className="w-4 h-4 text-teal-600" />
                    <h3 className="text-sm font-semibold text-slate-700">Submit Your Appeal</h3>
                    <span className="text-xs text-slate-400 ml-1">
                      ({completedActivities.size}/1)
                    </span>
                  </div>
                  <ChevronDown 
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                      isActivityExpanded ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                
                {/* Collapsible Content */}
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isActivityExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="px-4 pb-4">
                    <p className="text-sm text-slate-600 mb-4">
                      Learn how to submit your appeal form and access the official Texas protest form.
                    </p>
                    
                    {/* Activity Item */}
                    <button
                      onClick={() => handleActivitySelect('file-appeal')}
                      className={`w-full text-left p-3 rounded-xl transition-all hover:shadow-md ${
                        activeActivity === 'file-appeal'
                          ? 'bg-teal-200 border-2 border-teal-400 shadow-md'
                          : completedActivities.has('file-appeal')
                          ? 'bg-emerald-100 border-2 border-emerald-200'
                          : 'bg-white border-2 border-slate-100 hover:border-teal-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center text-teal-600">
                            <FileText className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">File Your Appeal</h4>
                            <p className="text-xs text-slate-500">Form download & submission methods</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {completedActivities.has('file-appeal') && (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          )}
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Back to Dashboard Button - after filing appeal, users should return to dashboard
                 to wait for their evidence packet before proceeding to Step 3 */}
              {calculateProgress() === 100 && (
                <div className="p-4 border-t border-slate-100">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full py-3 px-4 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
                  >
                    Back to Dashboard
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT SIDE - Video Player OR Activity */}
          <div className="lg:col-span-8 order-1 lg:order-2">
            {/* Show Activity when active */}
            {activeActivity ? (
              <FileAppealActivity
                onComplete={handleActivityComplete}
                onBack={handleBackFromActivity}
                isComplete={completedActivities.has('file-appeal')}
              />
            ) : (
              /* Show Video Player when no activity is active */
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                {getActiveVideo() ? (
                  <>
                    <VideoPlayer
                      key={activeSection}
                      videoUrl={getActiveVideo()?.videoUrl || ''}
                      onVideoComplete={handleVideoComplete}
                      currentProgress={0}
                      onProgress={() => {}}
                    />
                    <div className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-900 mb-2">
                            {getActiveVideo()?.title}
                          </h3>
                          <p className="text-slate-600">
                            {getActiveVideo()?.description}
                          </p>
                        </div>
                        {completedSections.has(getActiveVideo()?.id || '') && (
                          <div className="flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                            <CheckCircle2 className="w-4 h-4" />
                            Watched
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="aspect-video bg-slate-100 flex items-center justify-center">
                    <p className="text-slate-500">Select a video to start learning</p>
                  </div>
                )}
              </div>
            )}

            {/* Bottom Navigation */}
            <div className="mt-6 flex justify-between">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </button>
              
              {/* Right CTA: Next Video when viewing videos and more available, otherwise Continue to Step 3 when complete */}
              {(() => {
                // When viewing a video (not activity), check if there's a next one
                if (!activeActivity && activeSection) {
                  const currentIndex = videoSections.findIndex(s => s.id === activeSection);
                  const hasNextVideo = currentIndex < videoSections.length - 1;
                  
                  if (hasNextVideo) {
                    return (
                      <button
                        onClick={() => {
                          setActiveSection(videoSections[currentIndex + 1].id);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="px-6 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
                      >
                        Next Video
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    );
                  }
                }
                
                // Show Back to Dashboard when module is complete - users need to wait for
                // their evidence packet before proceeding to Step 3
                if (calculateProgress() === 100) {
                  return (
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="px-6 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
                    >
                      Back to Dashboard
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  );
                }
                
                return null;
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step1FilePage;
